'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff, Lock, Mail, AlertCircle, ArrowRight, ClipboardCheck, ArrowLeft, ShieldCheck } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

// Hardcoded assessment staff email allowlist — no role metadata dependency
const ASSESSMENT_EMAILS = [
  'assessment@zivasol.com',
  'christall.byfield@zivasol.com',
];

export default function AssessmentSignInClient() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const normalizedEmail = email.trim().toLowerCase();

    // Step 1: Check if this email is allowed before even hitting Supabase
    if (!ASSESSMENT_EMAILS.includes(normalizedEmail)) {
      setError('Access denied. This portal is for Assessment Managers only.');
      setLoading(false);
      return;
    }

    try {
      const supabase = createClient();

      // Step 2: Sign out any existing session first to avoid stale state
      await supabase.auth.signOut();

      // Step 3: Attempt sign in
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: password,
      });

      if (signInError) {
        setError('Invalid email or password. Please try again.');
        setLoading(false);
        return;
      }

      if (!data.user) {
        setError('Sign in failed. Please try again.');
        setLoading(false);
        return;
      }

      // Step 4: Double-check the signed-in email matches allowlist
      const signedInEmail = (data.user.email ?? '').toLowerCase();
      if (!ASSESSMENT_EMAILS.includes(signedInEmail)) {
        await supabase.auth.signOut();
        setError('Access denied. This portal is for Assessment Managers only.');
        setLoading(false);
        return;
      }

      // Step 5: Success — redirect to assessment dashboard
      window.location.href = '/assessment-dashboard';
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] xl:w-[480px] flex-shrink-0 bg-emerald-900 p-10 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
          <div className="absolute -bottom-16 right-10 w-64 h-64 rounded-full bg-emerald-600/20" />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-white/10">
              <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={48} height={48} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="block text-lg font-bold text-white">Ziva DocuSecure</span>
              <span className="block text-xs text-white/50">Niara Solutions</span>
            </div>
          </div>
          <div className="mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
              <ClipboardCheck size={28} className="text-emerald-300" />
            </div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">Assessment Manager Portal</h2>
            <p className="text-white/60 text-sm leading-relaxed">
              Review student documents, manage assessment records, and track programme submissions across all cohorts.
            </p>
          </div>
          <div className="space-y-3">
            {[
              'View all student submissions and documents',
              'Review uploaded documents in-app without downloading',
              'Track assessment records by programme',
              'Monitor document verification status',
              'Export student records and reports',
            ].map((f) => (
              <div key={f} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-emerald-400/20 flex items-center justify-center flex-shrink-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                </div>
                <span className="text-sm text-white/70">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10">
          <div className="h-px bg-white/10 mb-4" />
          <p className="text-xs text-white/30">docusecure.myzivasol.com · © 2026 Ziva Niara Solutions</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowLeft size={14} /> Back to Home
          </Link>

          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg overflow-hidden bg-primary/10">
              <Image src="/assets/images/logo-1778027352072.jpeg" alt="Ziva Niara Solutions logo" width={36} height={36} className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="block text-sm font-bold text-primary">Ziva DocuSecure</span>
              <span className="block text-[10px] text-muted-foreground">Niara Solutions</span>
            </div>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-bold text-foreground">Assessment Manager Sign In</h1>
            <p className="text-sm text-muted-foreground mt-1">Access assessment records and student documents</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label className="form-label">Email Address</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input pl-9"
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-9 pr-10"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">Sign In <ArrowRight size={15} /></span>
              )}
            </button>
          </form>

          <div className="mt-5 flex items-start gap-2 p-3 bg-secondary/40 border border-primary/15 rounded-xl">
            <ShieldCheck size={14} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Assessment Managers can view all student submissions and documents. Contact your administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
